import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatErr, fmtDate, todayLocal } from "@/lib/api";

const STATUS_STYLE = {
  active: { label: "Active", cls: "bg-status-green/10 text-status-green" },
  expired: { label: "Expired", cls: "bg-slate-100 text-slate-400" },
  revoked: { label: "Revoked", cls: "bg-status-red/10 text-status-red" },
};

export function TempAccessPanel({ classes }) {
  const [teachers, setTeachers] = useState([]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ teacher_id: "", class_id: "", date: todayLocal() });
  const [granting, setGranting] = useState(false);
  const [confirmId, setConfirmId] = useState("");

  useEffect(() => {
    api.get("/school/attendance/temp-access").then((r) => setRows(r.data.rows)).catch((e) => toast.error(formatErr(e)));
    api.get("/school/teachers").then((r) => setTeachers(r.data)).catch((e) => toast.error(formatErr(e)));
  }, []);

  const grant = async () => {
    if (!form.teacher_id || !form.class_id || !form.date) {
      toast.error("Select a teacher, class and date first");
      return;
    }
    setGranting(true);
    try {
      const { data } = await api.post("/school/attendance/temp-access", form);
      setRows(data.rows);
      toast.success("Temporary attendance access granted");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setGranting(false);
    }
  };

  const revoke = async (id) => {
    try {
      const { data } = await api.post(`/school/attendance/temp-access/${id}/revoke`);
      setRows(data.rows);
      setConfirmId("");
      toast.success("Assignment revoked");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <div data-testid="temp-access-section" className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h2 className="font-heading font-bold">Temporary Attendance Access</h2>
        <p className="text-xs text-slate-500 mt-1 mb-4">
          For when the regular class teacher is absent. Access applies only to the exact date chosen and expires automatically — no reset needed the next day.
          You can grant multiple teachers different classes on the same date, or one teacher several classes.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={form.teacher_id} onValueChange={(v) => setForm({ ...form, teacher_id: v })}>
            <SelectTrigger data-testid="temp-teacher-select" className="w-48 bg-white"><SelectValue placeholder="Select teacher" /></SelectTrigger>
            <SelectContent>
              {teachers.map((t) => (
                <SelectItem key={t.id} value={t.id} data-testid={`temp-teacher-option-${t.id}`}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })}>
            <SelectTrigger data-testid="temp-class-select" className="w-44 bg-white"><SelectValue placeholder="Select class" /></SelectTrigger>
            <SelectContent>
              {classes.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`temp-class-option-${c.id}`}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input type="date" data-testid="temp-date-input" value={form.date} min={todayLocal()} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-40 bg-white" />
          <Button data-testid="temp-grant-btn" onClick={grant} disabled={granting} className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold px-6 transition-transform duration-300 active:scale-95">
            {granting ? "Granting…" : "Grant Access"}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden overflow-x-auto" data-testid="temp-access-list">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
              {["Teacher", "Class / Section", "Date", "Status", "Action"].map((h) => <th key={h} className="px-5 py-3">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`temp-row-${r.id}`} className="border-t border-slate-100">
                <td className="px-5 py-3 font-semibold">{r.teacher_name}</td>
                <td className="px-5 py-3">{r.class_name}</td>
                <td className="px-5 py-3 text-slate-500">{fmtDate(r.date)}</td>
                <td className="px-5 py-3">
                  <span data-testid={`temp-status-${r.id}`} className={`text-xs font-bold px-2.5 py-1 rounded-full ${STATUS_STYLE[r.status].cls}`}>{STATUS_STYLE[r.status].label}</span>
                </td>
                <td className="px-5 py-3">
                  {r.status === "active" ? (
                    confirmId === r.id ? (
                      <span className="flex items-center gap-2">
                        <Button size="sm" data-testid={`temp-revoke-confirm-${r.id}`} onClick={() => revoke(r.id)} className="rounded-full bg-status-red text-white font-bold h-8 px-4">Confirm revoke</Button>
                        <button data-testid={`temp-revoke-cancel-${r.id}`} onClick={() => setConfirmId("")} className="text-xs font-semibold text-slate-400 hover:text-slate-600">Cancel</button>
                      </span>
                    ) : (
                      <Button variant="outline" size="sm" data-testid={`temp-revoke-${r.id}`} onClick={() => setConfirmId(r.id)} className="rounded-full font-bold border-slate-300 h-8 px-4">Revoke</Button>
                    )
                  ) : (
                    <span className="text-xs text-slate-300">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="text-sm text-slate-400 py-8 text-center" data-testid="temp-access-empty">No temporary assignments yet.</p>}
      </div>
    </div>
  );
}
